import React, { useEffect, useState } from 'react';
import { Card, Form, FormControl } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage } from '@fortawesome/free-solid-svg-icons';

import Button from '../../Parts/Button';
import AnnouncementPopup from '../AnnouncementPopup';
import {
	fromDatetimeLocalValue,
	hasCtaSchedule,
	getEditorTimezoneLabel,
	toDatetimeLocalValue,
} from '../scheduleUtils';

type AnnouncementPopupEditorProps = {
	heading: string;
	description: string;
	/** Prefix for the input ids so multiple editors on the page stay unique. */
	idPrefix: string;
	/** Mirrors the live popup: the second popup has no "do not show again" option. */
	allowHideUntilUpdate: boolean;
	title: string;
	body: string;
	imageUrl: string | null;
	scheduleStart: string | null;
	scheduleEnd: string | null;
	onTitleChange: (value: string) => void;
	onBodyChange: (value: string) => void;
	onImageChange: (value: string | null) => void;
	onScheduleStartChange: (value: string | null) => void;
	onScheduleEndChange: (value: string | null) => void;
	onSave: () => void;
	onClear: () => void;
	onCancel: () => void;
	saving?: boolean;
	clearing?: boolean;
};

const AnnouncementPopupEditor = ({
	heading,
	description,
	idPrefix,
	allowHideUntilUpdate,
	title,
	body,
	imageUrl,
	scheduleStart,
	scheduleEnd,
	onTitleChange,
	onBodyChange,
	onImageChange,
	onScheduleStartChange,
	onScheduleEndChange,
	onSave,
	onClear,
	onCancel,
	saving = false,
	clearing = false,
}: AnnouncementPopupEditorProps) => {
	const [previewing, setPreviewing] = useState(false);
	const [showSchedule, setShowSchedule] = useState(() => hasCtaSchedule({
		start: scheduleStart,
		end: scheduleEnd,
	}));

	useEffect(() => {
		setShowSchedule(hasCtaSchedule({ start: scheduleStart, end: scheduleEnd }));
	}, [scheduleStart, scheduleEnd]);

	const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = () => {
			if (typeof reader.result === 'string') {
				onImageChange(reader.result);
			}
		};
		reader.readAsDataURL(file);
	};

	const handleScheduleToggle = (checked: boolean) => {
		setShowSchedule(checked);
		if (!checked) {
			onScheduleStartChange(null);
			onScheduleEndChange(null);
		}
	};

	return (
		<Card body className="half-card mb-4">
			<h3>{heading}</h3>

			<p className="text-muted mb-0">
				<em>{description}</em>
			</p>

			<div className="mb-2 mt-3">
				<label className="d-block" htmlFor={`${idPrefix}-title`}>
					Title
				</label>
				<input
					id={`${idPrefix}-title`}
					className="form-control"
					value={title}
					onChange={(e) => onTitleChange(e.target.value)}
					placeholder="Popup headline"
				/>
			</div>

			<div className="mb-2">
				<span className="d-block">Image</span>
				<label
					className={
						'd-flex align-items-center justify-content-center position-relative '
						+ 'w-100 mb-0 overflow-hidden'
					}
					style={{
						height: 220,
						cursor: 'pointer',
						background: 'var(--bs-body-bg, #fff)',
						border: '1px solid var(--bs-border-color, #dedede)',
						borderRadius: 'var(--bs-border-radius, 0.375rem)',
					}}
				>
					<input
						type="file"
						accept="image/*"
						onChange={handleImageUpload}
						className="position-absolute opacity-0"
						style={{ inset: 0, cursor: 'pointer' }}
					/>
					{imageUrl ? (
						<img
							src={imageUrl}
							alt="Popup preview"
							className="w-100 h-100"
							style={{ objectFit: 'contain' }}
						/>
					) : (
						<span className="text-muted text-center">
							<FontAwesomeIcon icon={faImage} className="d-block mb-2" size="2x" />
							Click to upload an image
						</span>
					)}
				</label>
				{imageUrl ? (
					<button
						type="button"
						className="btn btn-link text-secondary p-0 mt-1 text-decoration-underline"
						onClick={() => onImageChange(null)}
					>
						Remove image
					</button>
				) : null}
			</div>

			<div className="mb-2">
				<label className="d-block" htmlFor={`${idPrefix}-body`}>
					Body
				</label>
				<FormControl
					as="textarea"
					id={`${idPrefix}-body`}
					className="form-control"
					value={body}
					onChange={(e) => onBodyChange(e.target.value)}
					placeholder="Main announcement message"
					rows={5}
				/>
			</div>

			<div className="mb-2 mt-3">
				<label htmlFor={`${idPrefix}-schedule-switch`}>Scheduled Announcement?</label>
				<Form.Switch
					id={`${idPrefix}-schedule-switch`}
					checked={showSchedule}
					onChange={(e) => handleScheduleToggle(e.target.checked)}
				/>
			</div>

			<div className={`mb-2 mt-3 d-flex gap-4 ${showSchedule ? 'd-flex' : 'd-none'}`}>
				<div className="flex-fill">
					<label className="d-block" htmlFor={`${idPrefix}-schedule-start`}>Start</label>
					<input
						id={`${idPrefix}-schedule-start`}
						type="datetime-local"
						className="form-control"
						value={toDatetimeLocalValue(scheduleStart)}
						onChange={(e) => onScheduleStartChange(fromDatetimeLocalValue(e.target.value))}
					/>
				</div>
				<div className="flex-fill">
					<label className="d-block" htmlFor={`${idPrefix}-schedule-end`}>End</label>
					<input
						id={`${idPrefix}-schedule-end`}
						type="datetime-local"
						className="form-control"
						value={toDatetimeLocalValue(scheduleEnd)}
						onChange={(e) => onScheduleEndChange(fromDatetimeLocalValue(e.target.value))}
					/>
				</div>
			</div>
			{showSchedule ? (
				<small className="text-muted d-block">
					Times use your device timezone ({getEditorTimezoneLabel()}).
				</small>
			) : null}

			<div className="mt-4 d-flex justify-content-end flex-wrap gap-2">
				<Button onClick={() => setPreviewing(true)} color="green" disabled={saving || clearing}>
					Preview
				</Button>
				<Button color="gray" onClick={onCancel} disabled={saving || clearing}>
					Reset changes
				</Button>
				<Button
					color="danger"
					onClick={onClear}
					loading={clearing}
					disabled={saving || (!title.trim() && !body.trim() && !imageUrl && !hasCtaSchedule({
						start: scheduleStart,
						end: scheduleEnd,
					}))}
				>
					Remove popup
				</Button>
				<Button color="green" onClick={onSave} loading={saving} disabled={clearing}>
					Save popup
				</Button>
			</div>
			{previewing ? (
				<AnnouncementPopup
					title={title}
					body={body}
					imageUrl={imageUrl}
					preview
					allowHideUntilUpdate={allowHideUntilUpdate}
					checkboxId={`${idPrefix}-preview-hide-until-update`}
					onClose={() => setPreviewing(false)}
				/>
			) : null}
		</Card>
	);
};

export default AnnouncementPopupEditor;
